// Original types from the energy library with scientific improvements
export type EnergyBlockType = "power" | "focus" | "drift" | "rest" | "ignore" | "ambient" | "any";

export interface EnergyBlock {
  type: EnergyBlockType;
  label: string;
  description: string;
  icon: string;
  color: string;
  percentage: number;
}

export const ENERGY_THRESHOLDS = {
  POWER: 80,
  FOCUS: 60,
  DRIFT: 30,
  AMBIENT: 75
};

// Update LegacyEnergyLevelType to include all variations used in the codebase
export type LegacyEnergyLevelType = 'high' | 'good' | 'medium' | 'moderate' | 'low';

// Update the BaselineInput to include both wakeTime and wakeTimeHour to support both APIs
export interface BaselineInput {
  wakeTime?: string;
  sleepTime?: string;
  chronotype?: string;
  wakeTimeHour?: number; // Added for compatibility
  sleepDuration?: number; // Added for compatibility
  useShiftedCurve?: boolean; // Added for compatibility
}

export interface WearableInput {
  hrvScore?: number;
  sleepScore?: number;
  readinessScore?: number;
  recoveryScore?: number;
  sleepDurations?: number[];
  sleepEfficiencies?: number[];
  hrvValues?: number[];
  rhrValues?: number[];
  remMinutes?: number[];
  deepMinutes?: number[];
}

export interface EnergyDataPoint {
  time: string;
  value: number;
  type?: EnergyBlockType;
  // Add the properties needed for availability calculations
  hour?: number;
  level?: number;
  block?: EnergyBlockType;
  state?: EnergyState;
  timestamp?: Date;
  energy?: number;
  factors?: EnergyFactors;
}

export interface EnergyForecast {
  data: EnergyDataPoint[];
  date: string;
  settings?: UserEnergySettings;
  chronotype?: Chronotype;
  hourly_predictions?: EnergyDataPoint[];
  peak_times?: Date[];
  dip_times?: Date[];
  optimal_sleep_window?: [Date, Date];
  sleep_debt_projection?: number;
}

export const ENERGY_TYPE_MAPPING: Record<LegacyEnergyLevelType, EnergyBlockType> = {
  high: 'power',
  good: 'focus', // Added mapping for 'good'
  medium: 'focus',
  moderate: 'focus', // Add mapping for 'moderate' to 'focus'
  low: 'drift'
};

export interface UserEnergySettings {
  chronotype?: string;
  wakeTime?: string;
  sleepTime?: string;
  peakStart?: string;
  peakEnd?: string;
  slumpStart?: string;
  slumpEnd?: string;
  secondWindStart?: string;
  secondWindEnd?: string;
}

// Extended types from the new implementation
export enum Chronotype {
  BEAR = 'BEAR',
  LION = 'LION', 
  WOLF = 'WOLF',
  DOLPHIN = 'DOLPHIN'
}

export interface SleepData {
  bedtime: Date;
  wakeTime: Date;
  sleepDuration: number;
  sleepEfficiency?: number;
  deepSleepPercentage?: number;
  remSleepPercentage?: number;
  awakenings?: number;
  sleepDebt: number;
  sleepQuality?: number;
  nightWakings?: number;
  sleepLatency?: number;
}

export interface WearableData {
  hrvScore?: number;
  sleepScore?: number;
  readinessScore?: number;
  recoveryScore?: number;
  sleepDurations?: number[];
  sleepEfficiencies?: number[];
  hrvValues?: number[];
  rhrValues?: number[];
  remMinutes?: number[];
  deepMinutes?: number[];
  stressLevel?: number;
  activityLevel?: number;
  temperature?: number;
  heartRateVariability?: number;
  restingHeartRate?: number;
  bodyTemperature?: number;
  lightExposure?: number;
  movementData?: any[];
}

export interface EnergyLevel {
  timestamp: Date;
  value: number;
  energy: number;
  confidence: number;
  factors: EnergyFactors;
  state?: EnergyState;
}

export interface EnergyFactors {
  circadianDrive: number;
  sleepPressure: number;
  sleepDebt: number;
  sleepDebtImpact?: number;
  cortisol?: number;
  melatonin?: number;
  wearableFactors?: WearableFactors;
}

export interface WearableFactors {
  hrv: number;
  restingHeartRate: number;
  bodyTemperature: number;
  activityLevel: number;
}

export interface CircadianParameters {
  chronotype: Chronotype;
  phase: number;
  amplitude: number;
  period: number;
}

export enum EnergyState {
  ASLEEP = 'ASLEEP',
  PEAK = 'PEAK',
  AWAKE_HIGH = 'AWAKE_HIGH',
  AWAKE_MODERATE = 'AWAKE_MODERATE',
  AWAKE_LOW = 'AWAKE_LOW'
}

export interface SAFTEComponents {
  processS: number;
  processC: number;
  sleepDebt: number;
  wearableAdjustment: number;
}

export interface HormoneLevels {
  cortisol: number;
  melatonin: number;
  timestamp: Date;
}

// Chronotype profiles with scientific parameters
export const CHRONOTYPE_PROFILES = {
  [Chronotype.BEAR]: {
    name: 'Bear',
    percentage: 55,
    description: 'Most common chronotype, follows traditional sleep schedule',
    optimalSleep: { start: '22:00', end: '07:00' },
    optimal_sleep: '22:00',
    optimal_wake: '07:00',
    peakEnergyWindow: { start: 10, end: 12 },
    energy_peak_start: 10,
    energy_peak_end: 12,
    lowEnergyWindow: { start: 14, end: 16 },
    afternoon_dip_start: 14,
    afternoon_dip_end: 16,
    phaseShift: 0,
    energyPattern: 'steady-peak-dip-recovery',
    cortisol_peak_offset: 1,
    melatonin_onset_offset: 2
  },
  [Chronotype.LION]: {
    name: 'Lion',
    percentage: 25,
    description: 'Early risers, most productive in morning',
    optimalSleep: { start: '21:00', end: '06:00' },
    optimal_sleep: '21:00',
    optimal_wake: '06:00',
    peakEnergyWindow: { start: 6, end: 10 },
    energy_peak_start: 6,
    energy_peak_end: 10,
    lowEnergyWindow: { start: 13, end: 16 },
    afternoon_dip_start: 13,
    afternoon_dip_end: 16,
    phaseShift: -2,
    energyPattern: 'early-peak-steady-decline',
    cortisol_peak_offset: 0,
    melatonin_onset_offset: 1
  },
  [Chronotype.WOLF]: {
    name: 'Wolf',
    percentage: 15,
    description: 'Night owls, peak performance in evening',
    optimalSleep: { start: '24:00', end: '08:00' },
    optimal_sleep: '00:00',
    optimal_wake: '08:00',
    peakEnergyWindow: { start: 18, end: 22 },
    energy_peak_start: 18,
    energy_peak_end: 22,
    lowEnergyWindow: { start: 8, end: 12 },
    afternoon_dip_start: 8,
    afternoon_dip_end: 12,
    phaseShift: 3,
    energyPattern: 'slow-build-evening-peak',
    cortisol_peak_offset: 2,
    melatonin_onset_offset: 4
  },
  [Chronotype.DOLPHIN]: {
    name: 'Dolphin',
    percentage: 10,
    description: 'Light sleepers, irregular patterns, often insomniacs',
    optimalSleep: { start: '23:30', end: '06:30' },
    optimal_sleep: '23:30',
    optimal_wake: '06:30',
    peakEnergyWindow: { start: 10, end: 14 },
    energy_peak_start: 10,
    energy_peak_end: 14,
    lowEnergyWindow: { start: 6, end: 9 },
    afternoon_dip_start: 6,
    afternoon_dip_end: 9,
    phaseShift: 0.5,
    energyPattern: 'irregular-variable',
    cortisol_peak_offset: 1.5,
    melatonin_onset_offset: 1.5
  }
};

// Energy algorithm constants
export const ENERGY_CONSTANTS = {
  SLEEP_ENERGY_MIN: 5,
  SLEEP_ENERGY_MAX: 15,
  WAKE_ENERGY_MIN: 20,
  PEAK_ENERGY_MAX: 90,
  SLEEP_DEBT_MAX_IMPACT: 30,
  WEARABLE_ADJUSTMENT_RANGE: 20,
  CIRCADIAN_AMPLITUDE: 40,
  SLEEP_PRESSURE_BUILDUP_RATE: 1.5,
  SLEEP_PRESSURE_DECAY_RATE: 0.8,
  CORTISOL_PEAK_HOUR: 8,
  MELATONIN_ONSET_OFFSET: 2
};

// Validation thresholds for testing
export const VALIDATION_THRESHOLDS = {
  DIP_MINIMUM_REDUCTION: 10,
  TRANSITION_SMOOTHNESS: 25,
  PEAK_MINIMUM_ENERGY: 70,
  SLEEP_ENERGY_RANGE: [5, 15],
  WAKE_ENERGY_MINIMUM: 20
};
